import type { Anomaly, Severity } from '@soc/shared';
import { SEVERITY_WEIGHT } from '@soc/shared';

export interface Cluster {
  /** Stable-ish id for the React key: `${ruleId}:${firstAlertId}`. */
  id: string;
  ruleId: string;
  /** Centroid (mean lng/lat) the cluster marker sits on. */
  lng: number;
  lat: number;
  /** Members; `alerts[0]` is the "first", used to point the feed. */
  alerts: Anomaly[];
  /** Most severe severity present — tints the cluster avatar. */
  topSeverity: Severity;
}

export type ClusterOrPin =
  | { kind: 'pin'; alert: Anomaly }
  | { kind: 'cluster'; cluster: Cluster };

/** Minimal slice of the maplibre map we need: lng/lat → screen pixels. */
export interface Projector {
  project(lngLat: [number, number]): { x: number; y: number };
}

/** Pins closer than this (in screen px, at the current zoom) merge into a cluster. */
const PX_THRESHOLD = 44;

/**
 * Group active alerts by ruleId, then greedily merge same-rule alerts whose
 * screen positions (at the current zoom) fall within {@link PX_THRESHOLD}.
 * Singletons are emitted as pins; multi-member groups as clusters at their
 * centroid. O(n²) per rule-group — trivially cheap at the ≤200-pin scale.
 * Requires a loaded map for `project()`; callers guard on that.
 */
export function clusterAlerts(alerts: Anomaly[], map: Projector): ClusterOrPin[] {
  const byRule = new Map<string, Anomaly[]>();
  for (const a of alerts) {
    const group = byRule.get(a.ruleId);
    if (group) group.push(a);
    else byRule.set(a.ruleId, [a]);
  }

  const out: ClusterOrPin[] = [];
  for (const [ruleId, group] of byRule) {
    const pts = group.map((a) => ({ a, p: map.project([a.lng, a.lat]) }));
    const used = new Array(pts.length).fill(false);

    for (let i = 0; i < pts.length; i++) {
      if (used[i]) continue;
      used[i] = true;
      const members = [pts[i].a];
      for (let j = i + 1; j < pts.length; j++) {
        if (used[j]) continue;
        const dx = pts[i].p.x - pts[j].p.x;
        const dy = pts[i].p.y - pts[j].p.y;
        if (dx * dx + dy * dy <= PX_THRESHOLD * PX_THRESHOLD) {
          members.push(pts[j].a);
          used[j] = true;
        }
      }

      if (members.length === 1) {
        out.push({ kind: 'pin', alert: members[0] });
      } else {
        const lng = members.reduce((s, a) => s + a.lng, 0) / members.length;
        const lat = members.reduce((s, a) => s + a.lat, 0) / members.length;
        const topSeverity = members.reduce<Severity>(
          (best, a) => (SEVERITY_WEIGHT[a.severity] < SEVERITY_WEIGHT[best] ? a.severity : best),
          members[0].severity,
        );
        out.push({
          kind: 'cluster',
          cluster: { id: `${ruleId}:${members[0].id}`, ruleId, lng, lat, alerts: members, topSeverity },
        });
      }
    }
  }
  return out;
}
